import { useEffect, useMemo, useState } from "react";
import { format, subDays, startOfMonth } from "date-fns";
import { api, apiError, currencyFmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DateField } from "@/components/DateField";
import { StatCard } from "@/components/StatCard";
import { Download, X, ReceiptText, DollarSign, Percent, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";

const toKey = (d) => (d ? format(d, "yyyy-MM-dd") : "");

const ETYPE_COLORS = {
  personal: "hsl(217 91% 60%)",
  company_credit_card: "hsl(262 83% 62%)",
  company_debit_card: "hsl(160 84% 39%)",
  unknown: "hsl(var(--muted-foreground))",
};

export default function Reports() {
  const { user } = useAuth();
  const [status, setStatus] = useState("approved");
  const [category, setCategory] = useState("all");
  const [expenseType, setExpenseType] = useState("all");
  const [branch, setBranch] = useState("all");
  const [from, setFrom] = useState(undefined);
  const [to, setTo] = useState(undefined);
  const [categories, setCategories] = useState([]);
  const [branches, setBranches] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const canBranch = user.role === "admin" || user.role === "manager";

  useEffect(() => {
    api.get("/meta/categories").then(({ data }) => setCategories(data.categories || [])).catch(() => {});
    if (canBranch) api.get("/branches").then(({ data }) => setBranches(data)).catch(() => {});
  }, [canBranch]);

  const params = useMemo(() => {
    const p = {};
    if (status !== "all") p.status = status;
    if (category !== "all") p.category = category;
    if (expenseType !== "all") p.expense_type = expenseType;
    if (branch !== "all") p.branch = branch;
    if (from) p.date_from = toKey(from);
    if (to) p.date_to = toKey(to);
    return p;
  }, [status, category, expenseType, branch, from, to]);

  useEffect(() => {
    setLoading(true);
    api.get("/reports/monthly", { params })
      .then(({ data }) => setReport(data))
      .catch((err) => toast.error(apiError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [params]);

  const applyPreset = (preset) => {
    const today = new Date();
    if (preset === "7d") { setFrom(subDays(today, 6)); setTo(today); }
    else if (preset === "month") { setFrom(startOfMonth(today)); setTo(today); }
  };

  const hasDates = from || to;

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/reports/monthly/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "reimbursement_report.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Report exported");
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Reports</h1>
          <p className="text-sm text-muted-foreground mt-1">Monthly reimbursement by category and overall.</p>
        </div>
        <Button variant="outline" className="rounded-sm" onClick={exportCsv} data-testid="report-export-csv">
          <Download className="h-4 w-4 mr-2" />Export CSV
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-3 bg-card border border-border rounded-md p-4">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Status</label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40 rounded-sm" data-testid="report-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Category</label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-52 rounded-sm" data-testid="report-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories (overall)</SelectItem>
              {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Expense Type</label>
          <Select value={expenseType} onValueChange={setExpenseType}>
            <SelectTrigger className="w-56 rounded-sm" data-testid="report-expense-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="personal">Personal Expense Claim</SelectItem>
              <SelectItem value="company_credit_card">Company Credit Card</SelectItem>
              <SelectItem value="company_debit_card">Company Debit Card</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {canBranch && (
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Branch</label>
            <Select value={branch} onValueChange={setBranch}>
              <SelectTrigger className="w-48 rounded-sm" data-testid="report-branch"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All branches</SelectItem>
                <SelectItem value="No branch">No branch</SelectItem>
                {branches.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">From</label>
          <DateField value={from} onChange={setFrom} placeholder="Start date" testId="report-date-from" className="w-40" />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">To</label>
          <DateField value={to} onChange={setTo} placeholder="End date" testId="report-date-to" className="w-40" />
        </div>
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="sm" className="rounded-sm" onClick={() => applyPreset("7d")} data-testid="report-preset-7d">Last 7 days</Button>
          <Button variant="outline" size="sm" className="rounded-sm" onClick={() => applyPreset("month")} data-testid="report-preset-month">This month</Button>
        </div>
        {hasDates && (
          <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => { setFrom(undefined); setTo(undefined); }} data-testid="report-date-clear">
            <X className="h-4 w-4 mr-1.5" />Clear dates
          </Button>
        )}
      </div>

      {loading || !report ? (
        <div className="text-muted-foreground text-sm">Loading report…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard testId="report-total" label="Total Reimbursement" value={currencyFmt(report.total)} Icon={DollarSign} />
            <StatCard testId="report-gst" label="Total GST" value={currencyFmt(report.total_gst)} Icon={Percent} />
            <StatCard testId="report-count" label="Claims" value={report.count} Icon={ReceiptText} />
          </div>

          <div className="bg-card border border-border rounded-md p-5">
            <h3 className="text-sm uppercase tracking-wider text-muted-foreground font-medium mb-4">Reimbursement by Month</h3>
            {report.by_month.length ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={report.by_month}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }}
                    formatter={(v) => currencyFmt(v)} />
                  <Bar dataKey="total" fill="hsl(var(--chart-1))" radius={[3, 3, 0, 0]} maxBarSize={72} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] grid place-items-center text-sm text-muted-foreground">
                <div className="text-center"><BarChart3 className="h-10 w-10 mx-auto mb-2 opacity-50" />No data for this selection.</div>
              </div>
            )}
          </div>

          {report.by_expense_type && (
            <div className="bg-card border border-border rounded-md p-5" data-testid="report-by-expense-type">
              <h3 className="text-sm uppercase tracking-wider text-muted-foreground font-medium mb-4">Spend by Expense Type</h3>
              {report.by_expense_type.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={report.by_expense_type} dataKey="total" nameKey="label" cx="50%" cy="50%"
                      innerRadius={60} outerRadius={100} paddingAngle={2}>
                      {report.by_expense_type.map((e) => (
                        <Cell key={e.expense_type} fill={ETYPE_COLORS[e.expense_type] || ETYPE_COLORS.unknown} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }}
                      formatter={(v) => currencyFmt(v)} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2">
                  {report.by_expense_type.map((e) => (
                    <div key={e.expense_type} className="flex items-center justify-between text-sm border-b border-border/60 pb-2" data-testid={`report-etype-${e.expense_type}`}>
                      <span className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: ETYPE_COLORS[e.expense_type] || ETYPE_COLORS.unknown }} />
                        {e.label}
                        <span className="text-muted-foreground text-xs">({e.count})</span>
                      </span>
                      <span className="font-mono font-medium">{currencyFmt(e.total)}</span>
                    </div>
                  ))}
                </div>
              </div>
              ) : (
                <div className="h-[200px] grid place-items-center text-sm text-muted-foreground">No data for this selection.</div>
              )}
            </div>
          )}

          <div className="bg-card border border-border rounded-md overflow-hidden">
            <h3 className="text-sm uppercase tracking-wider text-muted-foreground font-medium p-5 pb-3">By Category</h3>
            {report.by_category.length ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">GST</TableHead>
                    <TableHead className="text-right">Claims</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.by_category.map((c) => (
                    <TableRow key={c.category} className="hover:bg-muted/50" data-testid={`report-cat-${c.category}`}>
                      <TableCell className="font-medium">{c.gl_code ? <><span className="font-mono text-xs text-muted-foreground mr-1.5">{c.gl_code}</span>{c.category}</> : c.category}</TableCell>
                      <TableCell className="text-right font-mono">{currencyFmt(c.total)}</TableCell>
                      <TableCell className="text-right font-mono text-muted-foreground">{currencyFmt(c.gst)}</TableCell>
                      <TableCell className="text-right font-mono">{c.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="p-8 text-center text-sm text-muted-foreground">No data for this selection.</div>
            )}
          </div>

          {canBranch && (
            <div className="bg-card border border-border rounded-md overflow-hidden">
              <h3 className="text-sm uppercase tracking-wider text-muted-foreground font-medium p-5 pb-3">By Branch</h3>
              {report.by_branch && report.by_branch.length ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Branch</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">GST</TableHead>
                      <TableHead className="text-right">Claims</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.by_branch.map((b) => (
                      <TableRow key={b.branch} className="hover:bg-muted/50" data-testid={`report-branch-${b.branch}`}>
                        <TableCell className="font-medium">{b.branch}</TableCell>
                        <TableCell className="text-right font-mono">{currencyFmt(b.total)}</TableCell>
                        <TableCell className="text-right font-mono text-muted-foreground">{currencyFmt(b.gst)}</TableCell>
                        <TableCell className="text-right font-mono">{b.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="p-8 text-center text-sm text-muted-foreground">No data for this selection.</div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
